/**
 * AgentsMdModal — editor for AGENTS.md project context file.
 * Opened from HeaderContextMenu → "Rediger AGENTS.md".
 * Fetches existing content from /api/project-context/{conversationId},
 * lets the user edit it in a textarea, and saves it back.
 */
import { useEffect, useRef, useState } from 'react'
import { X, Save, Loader2, Bot, RefreshCw } from 'lucide-react'
import { apiFetch } from '@/lib/api'
import { API_BASE } from '@/lib/config'

interface AgentsMdModalProps {
  conversationId: string
  language: 'no' | 'en'
  onClose: () => void
}

const T = {
  no: {
    title: 'Rediger AGENTS.md',
    subtitle: 'Gi agenten persistent kontekst om prosjektet',
    save: 'Lagre',
    saving: 'Lagrer…',
    cancel: 'Avbryt',
    create: 'Opprett fra mal',
    creating: 'Oppretter…',
    saved: 'Lagret!',
    loadError: 'Kunne ikke laste AGENTS.md',
    saveError: 'Kunne ikke lagre AGENTS.md',
    placeholder: '# AGENTS.md\n\nBeskriv prosjektet ditt her…',
    hint: 'Agenten leser denne filen automatisk ved oppstart av nye kjøringer.',
  },
  en: {
    title: 'Edit AGENTS.md',
    subtitle: 'Give the agent persistent context about the project',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    create: 'Create from template',
    creating: 'Creating…',
    saved: 'Saved!',
    loadError: 'Could not load AGENTS.md',
    saveError: 'Could not save AGENTS.md',
    placeholder: '# AGENTS.md\n\nDescribe your project here…',
    hint: 'The agent reads this file automatically at the start of new runs.',
  },
}

export function AgentsMdModal({ conversationId, language, onClose }: AgentsMdModalProps) {
  const t = T[language] ?? T['no']
  const [content, setContent] = useState('')
  const [template, setTemplate] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [exists, setExists] = useState(false)
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  // Load existing content
  useEffect(() => {
    setLoading(true)
    apiFetch(`${API_BASE}/api/project-context/${conversationId}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then(data => {
        setContent(data.content ?? '')
        setTemplate(data.template ?? '')
        setExists(data.exists ?? false)
      })
      .catch(() => {
        setStatus('error')
      })
      .finally(() => setLoading(false))
  }, [conversationId])

  // Focus textarea on open
  useEffect(() => {
    if (!loading && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(0, 0)
    }
  }, [loading])

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose()
  }

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleSave = async () => {
    setSaving(true)
    setStatus('idle')
    try {
      const res = await apiFetch(`${API_BASE}/api/project-context/${conversationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setExists(true)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2500)
    } catch {
      setStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateFromTemplate = async () => {
    setCreating(true)
    setStatus('idle')
    try {
      const res = await apiFetch(`${API_BASE}/api/project-context/${conversationId}/create`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setContent(data.content ?? template)
      setExists(true)
    } catch {
      setStatus('error')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--ctx-bg, #1E1E20)',
          border: '1px solid var(--ctx-border, rgba(255,255,255,0.1))',
          borderRadius: 18,
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          width: '100%',
          maxWidth: 760,
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '16px 20px 14px',
          borderBottom: '1px solid var(--ctx-border, rgba(255,255,255,0.07))',
        }}>
          <Bot size={16} style={{ color: '#1A93FE', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ctx-text, #E5E5E5)' }}>{t.title}</div>
            <div style={{ fontSize: 11, color: 'var(--ctx-text-sub, #8A8A8A)', marginTop: 1 }}>{t.subtitle}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--ctx-text-sub, #8A8A8A)', padding: 4, borderRadius: 6,
              display: 'flex', alignItems: 'center',
            }}
            aria-label="Lukk"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 20px' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ctx-text-sub, #8A8A8A)', fontSize: 13 }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              Laster…
            </div>
          ) : (
            <>
              {!exists && !content && (
                <div style={{
                  marginBottom: 12, padding: '10px 14px',
                  background: 'rgba(26,147,254,0.08)',
                  border: '1px solid rgba(26,147,254,0.2)',
                  borderRadius: 10, fontSize: 12,
                  color: 'var(--ctx-text-sub, #8A8A8A)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Bot size={12} style={{ color: '#1A93FE', flexShrink: 0 }} />
                  <span>
                    {language === 'no'
                      ? 'Ingen AGENTS.md funnet. Opprett fra mal eller skriv din egen.'
                      : 'No AGENTS.md found. Create from template or write your own.'}
                  </span>
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={t.placeholder}
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: 320,
                  height: 'calc(88vh - 220px)',
                  maxHeight: 520,
                  padding: '12px 14px',
                  background: 'var(--ctx-hover, rgba(255,255,255,0.04))',
                  border: '1px solid var(--ctx-border, rgba(255,255,255,0.08))',
                  borderRadius: 10,
                  color: 'var(--ctx-text, #E5E5E5)',
                  fontSize: 12,
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                  lineHeight: 1.65,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ctx-text-sub, #6A6A6A)' }}>
                {t.hint}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 20px 16px',
          borderTop: '1px solid var(--ctx-border, rgba(255,255,255,0.07))',
        }}>
          {!exists && !loading && (
            <button
              onClick={handleCreateFromTemplate}
              disabled={creating}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8,
                border: '1px solid var(--ctx-border, rgba(255,255,255,0.1))',
                background: 'transparent',
                color: 'var(--ctx-text-sub, #9A9A9A)',
                fontSize: 12, cursor: creating ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: creating ? 0.6 : 1,
              }}
            >
              <RefreshCw size={12} />
              {creating ? t.creating : t.create}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {status === 'saved' && (
            <span style={{ fontSize: 12, color: '#22C55E' }}>{t.saved}</span>
          )}
          {status === 'error' && (
            <span style={{ fontSize: 12, color: '#EF4444' }}>{t.saveError}</span>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px', borderRadius: 8,
              border: '1px solid var(--ctx-border, rgba(255,255,255,0.1))',
              background: 'transparent',
              color: 'var(--ctx-text-sub, #C0C0C0)',
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 8, border: 'none',
              background: '#1A93FE', color: '#fff',
              fontSize: 13, cursor: saving || loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', fontWeight: 500,
              opacity: saving || loading ? 0.6 : 1,
            }}
          >
            {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} />}
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  )
}
